import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';
export function TermsAndConditionsPage() {
  usePageMeta('Terms & Conditions | OnlineCollegeGuide', 'Review the Terms of Use governing your access to and use of OnlineCollegeGuide.io.');
  return <main className="bg-white py-10 sm:py-16" data-id="element-1899">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-gray-800" data-id="element-1900">
        <Link to="/" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-navy mb-8 transition-colors" data-id="element-1901">
          <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-1902" />
          Back to home
        </Link>

        {/* Page header */}
        <header className="border-b border-gray-200 pb-8 mb-12" data-id="element-1903">
          <h1 className="text-4xl sm:text-5xl font-bold text-navy tracking-tight leading-tight" data-id="element-1904">
            Terms &amp; Conditions
          </h1>
          <p className="text-sm text-gray-500 mt-3" data-id="element-1905">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </header>

        {/* TOC */}
        <Section title="Table of Contents" data-id="element-1906">
          <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-1907">
            {tocItems.map(item => <li key={item.id} data-id="element-1908">
                <a href={`#${item.id}`} className="text-navy hover:text-crimson hover:underline transition-colors" data-id="element-1909">
                  {item.label}
                </a>
              </li>)}
          </ol>
        </Section>

        {/* 1 */}
        <NumberedSection id="section-1" number={1} title="Agreement to Terms" data-id="element-1910">
          <Paragraph data-id="element-1911">
            These Terms of Use constitute a legally binding agreement between
            you and onlinecollegeguide.io concerning your access to and use of
            the website. By accessing the site, you confirm that you have read,
            understood, and agree to be bound by these Terms of Use.{' '}
            <strong className="text-navy" data-id="element-1912">
              IF YOU DO NOT AGREE WITH ALL OF THESE TERMS OF USE, YOU ARE
              PROHIBITED FROM USING THE SITE AND MUST DISCONTINUE USE
              IMMEDIATELY.
            </strong>
          </Paragraph>
          <Paragraph data-id="element-1913">
            We reserve the right to make changes or modifications to these Terms
            of Use at any time and for any reason. We will alert you to any
            changes by updating the "Last updated" date above. Your continued
            use of the site after revised terms are posted constitutes
            acceptance of the changes.
          </Paragraph>
          <Paragraph data-id="element-1914">
            The site is intended for users who are at least 18 years old.
            Persons under 18 are not permitted to use or register for the site.
          </Paragraph>
        </NumberedSection>

        {/* 2 */}
        <NumberedSection id="section-2" number={2} title="Intellectual Property Rights" data-id="element-1915">
          <Paragraph data-id="element-1916">
            Unless otherwise indicated, the site is our proprietary property,
            and all source code, databases, functionality, software, website
            designs, audio, video, text, photographs, and graphics on the site
            are owned or controlled by us or licensed to us, and are protected
            by copyright and trademark laws.
          </Paragraph>
          <Paragraph data-id="element-1917">
            The content is provided "AS IS" for your information and personal
            use only. Except as expressly provided in these Terms of Use, no
            part of the site or content may be copied, reproduced, aggregated,
            republished, uploaded, posted, publicly displayed, encoded,
            translated, transmitted, distributed, sold, licensed, or otherwise
            exploited for any commercial purpose without our express prior
            written permission.
          </Paragraph>
          <Paragraph data-id="element-1918">
            You are granted a limited license to access and use the site and to
            download or print a copy of any portion of the content to which you
            have properly gained access solely for your personal, non-commercial
            use.
          </Paragraph>
        </NumberedSection>

        {/* 3 */}
        <NumberedSection id="section-3" number={3} title="User Representations" data-id="element-1919">
          <Paragraph data-id="element-1920">
            By using the site, you represent and warrant that:
          </Paragraph>
          <BulletList items={['All registration information you submit will be true, accurate, current, and complete', 'You will maintain the accuracy of such information and promptly update it as necessary', 'You have the legal capacity and agree to comply with these Terms of Use', 'You are not a minor in the jurisdiction in which you reside', 'You will not access the site through automated or non-human means', 'You will not use the site for any illegal or unauthorized purpose', 'Your use of the site will not violate any applicable law or regulation']} data-id="element-1921" />
        </NumberedSection>

        {/* 4 */}
        <NumberedSection id="section-4" number={4} title="Prohibited Activities" data-id="element-1922">
          <Paragraph data-id="element-1923">
            You may not access or use the site for any purpose other than that
            for which we make the site available. The site may not be used in
            connection with any commercial endeavors except those that are
            specifically endorsed or approved by us.
          </Paragraph>
          <Paragraph data-id="element-1924">As a user of the site, you agree not to:</Paragraph>
          <BulletList items={['Systematically retrieve data or other content from the site to create a collection, compilation, database, or directory', 'Make any unauthorized use of the site, including collecting usernames and/or email addresses by electronic or other means', 'Use the site to advertise or offer to sell goods and services', 'Circumvent, disable, or otherwise interfere with security-related features of the site', 'Engage in unauthorized framing of or linking to the site', 'Trick, defraud, or mislead us and other users', 'Interfere with, disrupt, or create an undue burden on the site or the networks or services connected to the site', 'Attempt to impersonate another user or person', 'Use any information obtained from the site to harass, abuse, or harm another person', 'Use the site in a manner inconsistent with any applicable laws or regulations']} data-id="element-1925" />
        </NumberedSection>

        {/* 5 */}
        <NumberedSection id="section-5" number={5} title="User Generated Contributions" data-id="element-1926">
          <Paragraph data-id="element-1927">
            The site may invite you to chat, contribute to, or participate in
            blogs, message boards, online forums, and other functionality. Any
            content you transmit to the site will be treated as non-confidential
            and non-proprietary.
          </Paragraph>
          <Paragraph data-id="element-1928">
            By posting content to the site, you grant us an unrestricted,
            unlimited, irrevocable, perpetual, non-exclusive, transferable,
            royalty-free, fully-paid, worldwide right to use, copy, reproduce,
            distribute, and display such content.
          </Paragraph>
        </NumberedSection>

        {/* 6 */}
        <NumberedSection id="section-6" number={6} title="Submissions" data-id="element-1929">
          <Paragraph data-id="element-1930">
            You acknowledge and agree that any questions, comments, suggestions,
            ideas, feedback, or other information regarding the site provided by
            you to us are non-confidential and shall become our sole property.
            We shall own exclusive rights, including all intellectual property
            rights, and shall be entitled to the unrestricted use and
            dissemination of these submissions for any lawful purpose,
            commercial or otherwise, without acknowledgment or compensation to
            you.
          </Paragraph>
        </NumberedSection>

        {/* 7 */}
        <NumberedSection id="section-7" number={7} title="Site Management" data-id="element-1931">
          <Paragraph data-id="element-1932">
            We reserve the right, but not the obligation, to:
          </Paragraph>
          <BulletList items={['Monitor the site for violations of these Terms of Use', 'Take appropriate legal action against anyone who violates the law or these Terms of Use', 'Refuse, restrict access to, limit availability of, or disable any of your contributions', 'Remove from the site or otherwise disable all files and content that are excessive in size or burdensome to our systems', 'Manage the site in a manner designed to protect our rights and property and to facilitate the proper functioning of the site']} data-id="element-1933" />
        </NumberedSection>

        {/* 8 */}
        <NumberedSection id="section-8" number={8} title="Privacy Policy" data-id="element-1934">
          <Paragraph data-id="element-1935">
            We care about data privacy and security. Please review our{' '}
            <Link to="/privacy-policy" className="text-navy underline hover:text-crimson font-medium" data-id="element-1936">
              Privacy Policy
            </Link>
            . By using the site, you agree to be bound by our Privacy Policy,
            which is incorporated into these Terms of Use.
          </Paragraph>
        </NumberedSection>

        {/* 9 */}
        <NumberedSection id="section-9" number={9} title="SMS Communications Terms" data-id="element-1937">
          <Paragraph data-id="element-1938">
            By providing your mobile phone number and submitting a form on our
            site, you agree to the following terms governing SMS/text message
            communications:
          </Paragraph>

          <SubHeading data-id="element-1939">Program Description</SubHeading>
          <Paragraph data-id="element-1940">
            OnlineCollegeGuide.io may send you autodialed and/or pre-recorded
            marketing text messages (SMS/MMS) regarding educational programs,
            follow-up inquiries, and promotional offers from us and our
            educational partners.
          </Paragraph>

          <SubHeading data-id="element-1941">Consent</SubHeading>
          <Paragraph data-id="element-1942">
            By submitting your information, you expressly consent to receive
            such messages at the mobile number provided. Consent is not a
            condition of any purchase or service. Your consent is governed by
            our Privacy Policy, which is incorporated herein by reference.
          </Paragraph>

          <SubHeading data-id="element-1943">Message Frequency</SubHeading>
          <Paragraph data-id="element-1944">
            Message frequency varies. You may receive multiple messages per
            inquiry depending on your interactions and program interests.
          </Paragraph>

          <SubHeading data-id="element-1945">Costs</SubHeading>
          <Paragraph data-id="element-1946">
            Message and data rates may apply. Contact your wireless carrier for
            details about your plan.
          </Paragraph>

          <SubHeading data-id="element-1947">Opt-Out</SubHeading>
          <Paragraph data-id="element-1948">
            You may opt out of SMS communications at any time by replying STOP
            to any message received. After opting out, you will receive a single
            confirmation message and no further SMS marketing messages will be
            sent unless you provide new consent.
          </Paragraph>

          <SubHeading data-id="element-1949">Assistance</SubHeading>
          <Paragraph data-id="element-1950">
            For help, reply HELP to any message or contact us through the
            information provided on our website.
          </Paragraph>

          <SubHeading data-id="element-1951">No Warranty for Delivery</SubHeading>
          <Paragraph data-id="element-1952">
            We do not guarantee that SMS messages will be delivered. Delivery is
            subject to effective transmission and your carrier's coverage and
            policies. We are not liable for delays or failures in SMS delivery.
          </Paragraph>

          <SubHeading data-id="element-1953">Third-Party Carriers</SubHeading>
          <Paragraph data-id="element-1954">
            You acknowledge that SMS transmission involves third-party carriers
            over whom we have no control. Standard carrier terms and conditions
            apply.
          </Paragraph>
        </NumberedSection>

        {/* 10 */}
        <NumberedSection id="section-10" number={10} title="Email Communications Terms" data-id="element-1955">
          <SubHeading data-id="element-1956">Program Description</SubHeading>
          <Paragraph data-id="element-1957">
            OnlineCollegeGuide.io may send you marketing and promotional emails
            regarding educational programs, follow-up inquiries, and offers from
            us and our educational partners. Marketing emails are sent from{' '}
            <span className="font-medium text-navy" data-id="element-1958">
              contact@onlinecollegeguide.io
            </span>
            .
          </Paragraph>

          <SubHeading data-id="element-1959">Consent</SubHeading>
          <Paragraph data-id="element-1960">
            By submitting your information on our site, you expressly consent to
            receive such emails at the address provided. Consent is not a
            condition of any purchase or service.
          </Paragraph>

          <SubHeading data-id="element-1961">Opt-Out</SubHeading>
          <Paragraph data-id="element-1962">
            You may opt out of marketing emails at any time by clicking the
            unsubscribe link included in every marketing email. Opt-out requests
            will be honored within 10 business days. You may also opt out by
            contacting us at the address below.
          </Paragraph>

          <SubHeading data-id="element-1963">CAN-SPAM Compliance</SubHeading>
          <Paragraph data-id="element-1964">
            All commercial emails we send will accurately identify the sender,
            include our physical mailing address, and include a working
            unsubscribe mechanism.
          </Paragraph>
        </NumberedSection>

        {/* 11 */}
        <NumberedSection id="section-11" number={11} title="Term and Termination" data-id="element-1965">
          <Paragraph data-id="element-1966">
            These Terms of Use shall remain in full force and effect while you
            use the site. We reserve the right to deny access to the site to
            anyone for any reason at any time without notice.
          </Paragraph>
          <Paragraph data-id="element-1967">
            If we terminate or suspend your access for any reason, you are
            prohibited from registering and creating a new account under your
            name or a borrowed name. We reserve the right to take appropriate
            legal action, including civil, criminal, and injunctive redress.
          </Paragraph>
        </NumberedSection>

        {/* 12 */}
        <NumberedSection id="section-12" number={12} title="Disclaimer" data-id="element-1968">
          <Paragraph data-id="element-1969">
            THE SITE IS PROVIDED ON AN AS-IS AND AS-AVAILABLE BASIS. YOU AGREE
            THAT YOUR USE OF THE SITE AND OUR SERVICES WILL BE AT YOUR SOLE
            RISK. TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL
            WARRANTIES, EXPRESS OR IMPLIED, IN CONNECTION WITH THE SITE AND YOUR
            USE THEREOF.
          </Paragraph>
          <Paragraph data-id="element-1970">
            WE MAKE NO WARRANTIES OR REPRESENTATIONS ABOUT THE ACCURACY OR
            COMPLETENESS OF THE SITE'S CONTENT OR THE CONTENT OF ANY WEBSITES
            LINKED TO THE SITE AND WE WILL ASSUME NO LIABILITY OR RESPONSIBILITY
            FOR ANY ERRORS, MISTAKES, OR INACCURACIES OF CONTENT AND MATERIALS.
          </Paragraph>
        </NumberedSection>

        {/* 13 */}
        <NumberedSection id="section-13" number={13} title="Limitations of Liability" data-id="element-1971">
          <Paragraph data-id="element-1972">
            IN NO EVENT WILL WE OR OUR DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE
            TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, CONSEQUENTIAL,
            EXEMPLARY, INCIDENTAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST
            PROFIT, LOST REVENUE, LOSS OF DATA, OR OTHER DAMAGES ARISING FROM
            YOUR USE OF THE SITE.
          </Paragraph>
        </NumberedSection>

        {/* 14 */}
        <NumberedSection id="section-14" number={14} title="Indemnification" data-id="element-1973">
          <Paragraph data-id="element-1974">
            You agree to defend, indemnify, and hold us harmless, including our
            subsidiaries, affiliates, and all of our respective officers,
            agents, partners, and employees, from and against any loss, damage,
            liability, claim, or demand, including reasonable attorneys' fees
            and expenses, made by any third party due to or arising out of your
            use of the site or breach of these Terms of Use.
          </Paragraph>
        </NumberedSection>

        {/* 15 */}
        <NumberedSection id="section-15" number={15} title="Governing Law" data-id="element-1975">
          <Paragraph data-id="element-1976">
            These Terms of Use and your use of the site are governed by and
            construed in accordance with the laws of the State of California,
            without regard to its conflict of law principles. Except for
            disputes subject to binding arbitration under the Dispute Resolution
            / Arbitration section below, you agree that any dispute arising out
            of or relating to these Terms shall be resolved exclusively in the
            state or federal courts located in Los Angeles County, California.
          </Paragraph>
        </NumberedSection>

        {/* 16 */}
        <NumberedSection id="section-16" number={16} title="Dispute Resolution / Arbitration" data-id="element-1977">
          <Paragraph data-id="element-1978">
            Any dispute, claim, or controversy arising out of or relating to
            these Terms or your use of the site, including any claims under the
            Telephone Consumer Protection Act (TCPA), shall be resolved by
            binding individual arbitration under the rules of the American
            Arbitration Association. You waive any right to bring or participate
            in a class action lawsuit or class-wide arbitration. This
            arbitration agreement does not apply to claims that may be brought
            in small claims court.
          </Paragraph>
        </NumberedSection>

        {/* 17 */}
        <NumberedSection id="section-17" number={17} title="Contact Us" data-id="element-1979">
          <Paragraph data-id="element-1980">
            If you have any questions or concerns about these Terms &amp;
            Conditions, please contact us at:
          </Paragraph>
          <div className="mt-5 p-6 bg-gray-50 border border-gray-200 rounded-xl" data-id="element-1981">
            <p className="font-bold text-navy text-lg" data-id="element-1982">Ironsmith Media</p>
            <p className="text-gray-700 mt-2" data-id="element-1983">
              1612 UPAS Street, San Diego, CA 92103
            </p>
            <p className="text-gray-700 mt-2" data-id="element-1984">
              Email:{' '}
              <a href="mailto:contact@onlinecollegeguide.io" className="text-navy underline hover:text-crimson font-medium" data-id="element-1985">
                contact@onlinecollegeguide.io
              </a>
            </p>
          </div>
        </NumberedSection>
      </div>
    </main>;
}
/* ---------- Sub-components ---------- */
function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="mt-2" data-id="element-1986">
      <h2 className="text-2xl sm:text-3xl font-bold text-navy mb-6 tracking-tight" data-id="element-1987">
        {title}
      </h2>
      <div className="space-y-4" data-id="element-1988">{children}</div>
    </section>;
}
function NumberedSection({
  id,
  number,
  title,
  children
}: {
  id: string;
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return <section id={id} className="mt-16 pt-10 border-t border-gray-200 scroll-mt-8" data-id="element-1989">
      <div className="flex items-baseline gap-3 mb-6" data-id="element-1990">
        <span className="text-crimson font-bold text-xl" data-id="element-1991">{number}.</span>
        <h2 className="text-2xl sm:text-3xl font-bold text-navy tracking-tight leading-tight" data-id="element-1992">
          {title}
        </h2>
      </div>
      <div className="space-y-5 leading-relaxed text-gray-700 text-base" data-id="element-1993">
        {children}
      </div>
    </section>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="text-lg font-bold text-navy mt-8 mb-3" data-id="element-1994">{children}</h3>;
}
function Paragraph({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-gray-700 leading-relaxed" data-id="element-1995">{children}</p>;
}
function BulletList({
  items
}: {
  items: string[];
}) {
  return <ul className="list-disc pl-6 space-y-2 marker:text-crimson" data-id="element-1996">
      {items.map(item => <li key={item} className="text-gray-700 leading-relaxed" data-id="element-1997">
          {item}
        </li>)}
    </ul>;
}
const tocItems = [{
  id: 'section-1',
  label: 'Agreement to Terms'
}, {
  id: 'section-2',
  label: 'Intellectual Property Rights'
}, {
  id: 'section-3',
  label: 'User Representations'
}, {
  id: 'section-4',
  label: 'Prohibited Activities'
}, {
  id: 'section-5',
  label: 'User Generated Contributions'
}, {
  id: 'section-6',
  label: 'Submissions'
}, {
  id: 'section-7',
  label: 'Site Management'
}, {
  id: 'section-8',
  label: 'Privacy Policy'
}, {
  id: 'section-9',
  label: 'SMS Communications Terms'
}, {
  id: 'section-10',
  label: 'Email Communications Terms'
}, {
  id: 'section-11',
  label: 'Term and Termination'
}, {
  id: 'section-12',
  label: 'Disclaimer'
}, {
  id: 'section-13',
  label: 'Limitations of Liability'
}, {
  id: 'section-14',
  label: 'Indemnification'
}, {
  id: 'section-15',
  label: 'Governing Law'
}, {
  id: 'section-16',
  label: 'Dispute Resolution / Arbitration'
}, {
  id: 'section-17',
  label: 'Contact Us'
}];